import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import classNames from 'classnames';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import {SpreadsheetLocaleSelector} from 'bulk-import/panes/SpreadsheetLocaleSelector';
import {DEFAULT_LANGUAGE_TAG} from 'i18n/LanguageTagConstants';
import FieldDefinitionInputOrDisplay from 'registry/field/FieldDefinitionInputOrDisplay';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {SchemaField, FieldType, FieldDefinition} from 'schemas/fields/SchemaFieldConstants';
import {simplifyRequiredLink} from 'schemas/fields/SchemaFieldUtil';
import * as SchemaFieldSelectorConstants from 'schemas/SchemaFieldSelectorConstants';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Checkbox from 'ui-library/Checkbox';
import {PURPLE} from 'ui-library/Colors';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {TextFieldType} from 'ui-library/inputs/EditableTextField';
import Modal from 'ui-library/modals/Modal';
import SettingsField, {SettingsLabel} from 'ui-library/SettingsField';

import {SimpleTable} from './SimpleTable';

function PurpleHourglassIcon(props: {addedStyle?: string}): JSX.Element {
  const className = props.addedStyle
    ? classNames(css(styles.purpleIcon), props.addedStyle)
    : css(styles.purpleIcon);
  return <BenchlingIcon icon="hourglass-half" className={className} />;
}

export function CheckboxInput(props: {
  label: string;
  name: string;
  checked: boolean;
  changeRequiredMigration?: boolean;
}): JSX.Element {
  return (
    <div className={css(styles.checkBoxCell)}>
      <Checkbox checked={props.checked} onChange={() => {}} disabled={true} />
      <label htmlFor={props.name} className={css(styles.checkboxLabel)}>
        {props.label} {props.changeRequiredMigration && <PurpleHourglassIcon />}
      </label>
    </div>
  );
}

export function shouldShowLocaleSelector(initialField: SchemaField, newField: SchemaField): boolean {
  const localizableTypes = new Set<String>([
    FieldType.INTEGER,
    FieldType.FLOAT,
    FieldType.DATE,
    FieldType.DATETIME,
  ]);
  if (initialField.fieldType === FieldType.TEXT && localizableTypes.has(newField.fieldType)) {
    return true;
  }
  return false;
}

interface BaseFieldMigrationCreationModalProps {
  onRequestSubmit: (settings: FieldMigrationSettings) => void;
  onRequestClose: () => void;
  initialField: SchemaField;
  newField: SchemaField;
  availableFieldOptions: Array<SchemaFieldConstants.FieldOption>;
  schemaFieldSelectors: Array<SchemaFieldSelectorConstants.SchemaFieldSelector>;
  shouldShowIsIntegration: boolean;
  shouldShowIsParentLink: boolean;
  isFieldDefinitionEditable: boolean;
}

export interface FieldMigrationCreationModalProps extends BaseFieldMigrationCreationModalProps {
  numItems: number | null;
}

export interface SchemaSpecificFieldMigrationCreationModalProps extends BaseFieldMigrationCreationModalProps {
  schemaId: string;
}

export interface FieldMigrationSettings {
  field: SchemaField;
  locale?: string;
}

export function FieldMigrationCreationModal(props: FieldMigrationCreationModalProps): JSX.Element {
  const {initialField, newField} = props;
  const shouldShowIsParentLink =
    props.shouldShowIsParentLink && TagSchemaUtil.isBioentityLinkFieldType(newField.fieldType);
  const numItemsElement = props.numItems ? `${props.numItems}` : <HorizontalThrobber />;
  const [locale, setLocale] = useState(DEFAULT_LANGUAGE_TAG.code);

  // We ignore the new fields name and system name as they can be changed at migration publish time
  const destinationFieldConfiguration: SchemaField = {
    ...newField,
    name: initialField.name,
    displayName: initialField.displayName,
    systemName: initialField.systemName,
  };
  const simplifiedInitialLink = simplifyRequiredLink(initialField.requiredLink);
  const simplifiedNewLink = simplifyRequiredLink(newField.requiredLink);
  const fieldTypeChanged =
    initialField.fieldType !== newField.fieldType || !isEqual(simplifiedInitialLink, simplifiedNewLink);
  const isMultiChanged = initialField.isMulti !== newField.isMulti;

  const [currentFieldType, setCurrentFieldType] = useState<SchemaFieldConstants.LeafFieldOption | null>(null);

  function getDestinationFieldWithChanges(): SchemaField {
    const {
      label,
      icon,
      fieldDefinition,
      disabled,
      metadata,
      ...fieldOptionProperties
    }: Partial<SchemaFieldConstants.SchemaField> & {
      fieldDefinition?: FieldDefinition;
      label?: string | JSX.Element;
      icon?: BenchlingIconName;
      disabled?: boolean;
      metadata?: string | null;
    } = currentFieldType ?? {};

    if (fieldDefinition) {
      assert(
        Object.values(FieldType).includes(fieldDefinition as FieldType),
        'Expected fieldDefinition to be a FieldType'
      );
      fieldOptionProperties.fieldType = fieldDefinition as FieldType;
    }

    return {
      ...destinationFieldConfiguration,
      ...fieldOptionProperties,
    };
  }

  return (
    <Modal
      title="Migrate Schema Field"
      onRequestSubmit={() => {
        props.onRequestSubmit({field: getDestinationFieldWithChanges(), locale});
      }}
      onRequestClose={props.onRequestClose}
      scrollable={true}
      mediumLarge={true}
      className="modal-text-only"
      buttons={[
        {text: 'Cancel', cancel: true},
        {text: 'Begin Migration', submit: true, style: ButtonStyle.PRIMARY},
      ]}
    >
      <div className={css(styles.nameSection)}>
        <div className={css(styles.nameField)}>
          <SettingsField
            isBeingCreated={false}
            isRequired={false}
            isWritable={false}
            label="Field Name"
            name="name"
            onChange={(value: string | number) => null}
            type={TextFieldType.TEXT}
            value={destinationFieldConfiguration.name}
            withLabelBottomMargin={true}
          />
        </div>
        <div className={css(styles.nameField)}>
          <SettingsField
            isBeingCreated={false}
            isRequired={false}
            isWritable={false}
            label="System Name"
            name="name"
            onChange={(value: string | number) => null}
            type={TextFieldType.TEXT}
            value={destinationFieldConfiguration.systemName}
            withLabelBottomMargin={true}
          />
        </div>
      </div>

      <div className={css(styles.nameSection)}>
        <div className={css(styles.nameField)}>
          <SettingsLabel
            label="Definition (Type)"
            helpIcon={
              fieldTypeChanged ? <PurpleHourglassIcon addedStyle={css(styles.paddingLeft)} /> : undefined
            }
          />
          <FieldDefinitionInputOrDisplay
            isFieldBeingCreated={true}
            availableFieldOptions={props.availableFieldOptions}
            schemaFieldSelectors={props.schemaFieldSelectors}
            currentFieldState={getDestinationFieldWithChanges()}
            inEditMode={props.isFieldDefinitionEditable}
            onSelectFieldOption={(fieldOption) => {
              setCurrentFieldType(fieldOption);
            }}
          />
        </div>
      </div>
      <div className={css(styles.checkboxRow)}>
        <CheckboxInput
          label="Required"
          name="isRequired"
          checked={destinationFieldConfiguration.isRequired}
        />
        <CheckboxInput
          label="Multi-select"
          name="isMulti"
          checked={destinationFieldConfiguration.isMulti}
          changeRequiredMigration={isMultiChanged}
        />
        {shouldShowIsParentLink && (
          <CheckboxInput
            label="Parent link"
            name="isParentLink"
            checked={destinationFieldConfiguration.isParentLink}
          />
        )}
        {props.shouldShowIsIntegration && (
          <CheckboxInput
            label="Integration"
            name="isIntegration"
            checked={destinationFieldConfiguration.isIntegration}
          />
        )}
      </div>
      <div className={css(styles.banner)}>
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          action={{actionType: InlineBannerActionType.ACCORDION}}
          text={
            <p>
              Some field changes (marked with <PurpleHourglassIcon />) require a field migration
            </p>
          }
          supportingInfo="To avoid data loss, this field will be archived and its data copied to a new field, where possible. No other changes can be made to this field during the migration."
        />
      </div>
      <div className={css(styles.tableContainer)}>
        <SimpleTable columns={['Items', 'Count']} values={[['Entities', numItemsElement]]} />
      </div>
      {shouldShowLocaleSelector(initialField, newField) && (
        <div className={css(styles.localeSelector)}>
          <SpreadsheetLocaleSelector languageTag={locale} onChange={setLocale} />
        </div>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  nameSection: {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
  },
  nameField: {
    width: '50%',
  },
  checkboxRow: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '16px',
  },
  checkBoxCell: {
    marginRight: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  checkboxLabel: {
    marginLeft: '8px',
    fontWeight: 'normal',
    marginBottom: '0',
  },
  banner: {
    marginTop: '16px',
  },
  tableContainer: {
    marginTop: '16px',
  },
  localeSelector: {
    marginTop: '16px',
  },
  purpleIcon: {
    color: PURPLE,
  },
  paddingLeft: {
    paddingLeft: 2,
  },
});
